/**
 * PATH: apps/qa/src/pages/reports/CreateDraftDialog.tsx
 * PURPOSE: Modal dialog for creating editable report drafts from inspections
 * WHY: Extracted from ReportsPage.tsx to keep files under 300 lines
 */

import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { inspectionApi, reportDraftApi } from '@/lib/api'
import {
  X,
  Loader2,
  Edit3,
} from 'lucide-react'

interface CreateDraftDialogProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (draftId: string) => void
}

export function CreateDraftDialog({ isOpen, onClose, onSuccess }: CreateDraftDialogProps) {
  const [selectedInspection, setSelectedInspection] = useState('')
  const [draftTitle, setDraftTitle] = useState('')

  const { data: inspectionsData, isLoading: inspectionsLoading } = useQuery({
    queryKey: ['qa-inspections-for-draft'],
    queryFn: () => inspectionApi.getInspections({ page_size: 100 }),
    enabled: isOpen,
  })

  const inspections = inspectionsData?.results || []

  const createMutation = useMutation({
    mutationFn: async () => {
      const selectedInsp = inspections.find((inspection) => inspection.id === selectedInspection)
      const title = draftTitle.trim() || `${selectedInsp?.inspection_name || 'QA'} Report`
      return reportDraftApi.createDraft({
        inspection: selectedInspection,
        title,
      })
    },
    onSuccess: (data) => {
      onSuccess(data.id)
    },
  })

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl p-6 w-full max-w-md shadow-xl border border-border animate-fadeIn">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Create Editable Report</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-accent transition-colors text-muted-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          Create a report draft with pre-populated sections from the inspection data.
          Edit each section before exporting to PDF.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Inspection <span className="text-destructive">*</span>
            </label>
            {inspectionsLoading ? (
              <div className="py-3 flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading inspections...
              </div>
            ) : (
              <select
                value={selectedInspection}
                onChange={(e) => setSelectedInspection(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">Select an inspection</option>
                {inspections.map((insp) => (
                  <option key={insp.id} value={insp.id}>
                    {insp.inspection_name} ({insp.status})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Report Title (optional)
            </label>
            <input
              type="text"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              placeholder="Auto-generated from inspection name"
              className="w-full px-3 py-2.5 rounded-xl border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-input text-foreground font-medium hover:bg-accent transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={!selectedInspection || createMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Edit3 className="w-4 h-4" />
                Create Draft
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
